/**
 * Shared breakdown views for the row model `breakdown.ts` builds (#1064 Wave
 * 3). Two exports:
 *
 *  - `PriceBreakdownRows` — the bare row list (item rows, per-fee explanation
 *    disclosures, the grouped-tax cluster). Checkout's `PriceOverview` and
 *    confirmation's `CostBreakdown` render exactly this; the booking inline
 *    block and (row-for-row) the price-details modal build on the same model.
 *  - `InlinePriceBreakdown` — the booking-sidebar chrome around those rows:
 *    heading (visible style) or accordion toggle with the live total riding
 *    on it (WP inline-price-breakdown.js render()).
 *
 * Disclosure open-state is controllable from the parent because the booking
 * overview unmounts while a re-quote settles: WP persists the three open
 * states on the widget element across full re-renders (dates, coupon,
 * rate-plan switch), so the React port lifts them to BookingForm, which stays
 * mounted. Surfaces that never unmount mid-session (checkout, confirmation)
 * just omit the props and get local state.
 *
 * All animation is the toolkit CSS's grid `0fr → 1fr` technique — content
 * stays mounted and `data-open`/`--open` drive the tween, so there is no
 * measured pixel height to go stale on re-render (see price-breakdown.css).
 */
import { useState } from 'react'
import type { BreakdownRow, ItemRow, ResolvedPriceBreakdownConfig } from './breakdown'

/** Controlled-when-provided, local-otherwise state pair. */
/**
 * Controlled-or-local state. The setter takes React's `SetStateAction`, not a
 * bare value: two toggles dispatched in one batch must not both resolve against
 * the same captured map (the second would clobber the first), so the updater is
 * forwarded to the owner untouched — `useState`'s setter accepts it directly.
 */
function useControllable<T>(
  controlled: T | undefined,
  onChange: ((value: T | ((prev: T) => T)) => void) | undefined,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [local, setLocal] = useState<T>(initial)
  if (controlled !== undefined && onChange) return [controlled, onChange]
  if (process.env.NODE_ENV !== 'production' && controlled !== undefined && !onChange) {
    // Silently falling back to local state makes the control look frozen.
    console.warn('[price-breakdown] a controlled value was passed without its onChange — the value is being ignored.')
  }
  return [local, setLocal]
}

interface RowLineProps {
  label: React.ReactNode
  amount: number
  formatPrice: (amount: number) => string
  sub?: boolean
  explanation?: string | null
  expKey?: string
  expOpen?: boolean
  onToggleExp?: (key: string, open: boolean) => void
}

function RowLine({ label, amount, formatPrice, sub, explanation, expKey, expOpen, onToggleExp }: RowLineProps) {
  return (
    <>
      <div className={`hr-pb-row${sub ? ' hr-pb-row--sub' : ''}`}>
        <span className="hr-pb-label">
          {label}
          {explanation && expKey !== undefined && (
            <button
              type="button"
              className="hr-pb-exp-toggle"
              aria-expanded={expOpen ? 'true' : 'false'}
              aria-label="About this charge"
              onClick={() => onToggleExp?.(expKey, !expOpen)}
            >
              <span className="hr-pb-exp-icon" aria-hidden="true">i</span>
            </button>
          )}
        </span>
        {/* Negative amounts (discounts) get a host-styleable hook — checkout
            colored them before the consolidation and keeps doing so. */}
        <span className={`hr-pb-amount${amount < 0 ? ' hr-pb-amount--negative' : ''}`}>{formatPrice(amount)}</span>
      </div>
      {explanation && (
        <div className="hr-pb-explanation" data-open={expOpen ? 'true' : 'false'}>
          <div>{explanation}</div>
        </div>
      )}
    </>
  )
}

export interface PriceBreakdownRowsProps {
  rows: BreakdownRow[]
  formatPrice: (amount: number) => string
  /** Display relabeling (priceLabelReplacements), exact-match on the original
   * title — applied to item titles and grouped-mode bucket labels, the same
   * two places WP's label-replacement filter reaches. */
  relabel?: (title: string) => string
  /** Unique prefix for aria-controls ids when several breakdowns share a page. */
  idPrefix: string
  /** Per-fee explanation open state (key: the row's `expKey`). Controlled
   * when provided with its onChange; local state otherwise. */
  expOpen?: Record<string, boolean>
  onExpOpenChange?: (next: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void
  /** Grouped-tax cluster open state. */
  taxOpen?: boolean
  onTaxOpenChange?: (open: boolean | ((prev: boolean) => boolean)) => void
}

export function PriceBreakdownRows({
  rows, formatPrice, relabel, idPrefix,
  expOpen, onExpOpenChange, taxOpen, onTaxOpenChange,
}: PriceBreakdownRowsProps) {
  const [expMap, setExpMap] = useControllable(expOpen, onExpOpenChange, {})
  const [taxIsOpen, setTaxOpen] = useControllable(taxOpen, onTaxOpenChange, false)
  const rename = relabel ?? ((title: string) => title)
  // Functional update, not `{ ...expMap }`: two toggles dispatched in one React
  // batch both spread the same closure-captured map and the second wins, so one
  // disclosure never opens. (PriceDetailsModal always did this correctly.)
  const toggleExp = (key: string, open: boolean) =>
    setExpMap((m) => ({ ...m, [key]: open }))

  const itemLabel = (row: ItemRow) =>
    row.rentMath
      ? `${formatPrice(row.rentMath.perNight)} × ${row.rentMath.nights} ${row.rentMath.nights === 1 ? 'night' : 'nights'}`
      : rename(row.title)

  const taxesId = `${idPrefix}-taxes`

  return (
    <div className="hr-pb-body">
      {rows.map((row, i) => {
        if (row.kind === 'item') {
          return (
            <RowLine
              key={`item-${i}`}
              label={itemLabel(row)}
              amount={row.amount}
              formatPrice={formatPrice}
              explanation={row.explanation}
              expKey={row.expKey}
              expOpen={expMap[row.expKey] === true}
              onToggleExp={toggleExp}
            />
          )
        }
        if (row.kind === 'group') {
          // Grouped rows get no explanation affordance: a combined "Fee(s):"
          // line has no single fee to explain.
          return <RowLine key={`group-${i}`} label={rename(row.label)} amount={row.amount} formatPrice={formatPrice} />
        }
        return (
          <div key={`taxes-${i}`}>
            <div className="hr-pb-row hr-pb-row--sub">
              <span className="hr-pb-label">
                <button
                  type="button"
                  className="hr-pb-tax-toggle"
                  aria-expanded={taxIsOpen ? 'true' : 'false'}
                  aria-controls={taxesId}
                  onClick={() => setTaxOpen(!taxIsOpen)}
                >
                  {row.label}
                  <span className="hr-pb-tax-caret" aria-hidden="true" />
                </button>
              </span>
              <span className="hr-pb-amount">{formatPrice(row.total)}</span>
            </div>
            <div className="hr-pb-taxes" id={taxesId} data-open={taxIsOpen ? 'true' : 'false'}>
              <div>
                {row.items.map((tax, j) => (
                  <RowLine
                    key={`tax-${j}`}
                    label={rename(tax.title)}
                    amount={tax.amount}
                    formatPrice={formatPrice}
                    explanation={tax.explanation}
                    expKey={tax.expKey}
                    expOpen={expMap[tax.expKey] === true}
                    onToggleExp={toggleExp}
                  />
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export interface InlinePriceBreakdownProps extends Omit<PriceBreakdownRowsProps, 'relabel'> {
  config: Pick<ResolvedPriceBreakdownConfig, 'style' | 'heading' | 'accordionLabel' | 'openByDefault'>
  relabel?: (title: string) => string
  /** The widget's displayed total, preformatted, repeated on the collapsed
   * accordion toggle so collapsing never hides the number. Null/'' = omitted
   * entirely (an empty figure next to a price is worse than none — WP
   * totalText()). */
  collapsedTotal?: string | null
  /** Accordion open state. `undefined` means "never toggled" — only then does
   * the admin's openByDefault choice apply (WP hrtkIpbOpen contract). */
  open?: boolean
  onOpenChange?: (open: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * The booking-sidebar inline block: heading + rows (visible style) or
 * accordion toggle + clipped rows. Renders nothing when there are no rows —
 * the caller should already have skipped mounting, but a rate-plan switch can
 * empty the model mid-flight and an empty shell must never insert.
 */
export function InlinePriceBreakdown({
  config, collapsedTotal, open, onOpenChange, ...rowsProps
}: InlinePriceBreakdownProps) {
  const [localOpen, setLocalOpen] = useState<boolean | undefined>(undefined)
  const stored = onOpenChange ? open : localOpen
  const setOpen = onOpenChange ?? setLocalOpen
  if (rowsProps.rows.length === 0) return null

  const accordion = config.style === 'accordion'
  // Undefined means "never toggled", which is not the same as "toggled
  // closed": only then does the admin's open-by-default choice apply.
  const isOpen = !accordion || (stored === undefined ? config.openByDefault : stored)
  // An explicitly configured accordion label outranks the state-reflecting
  // defaults and is used verbatim in BOTH states (WP Settings.php quirk,
  // deliberate: a noun phrase like "What you pay" can't be verb-prefixed;
  // state stays legible through the chevron and aria-expanded).
  const toggleLabel = config.accordionLabel ||
    (isOpen ? 'Hide price breakdown' : 'Show price breakdown')
  const panelId = `${rowsProps.idPrefix}-panel`

  return (
    <div className={`hr-pb${accordion ? ' hr-pb--accordion' : ''}${isOpen ? ' hr-pb--open' : ''}`}>
      {accordion ? (
        <button
          type="button"
          className="hr-pb-toggle"
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-controls={panelId}
          onClick={() => setOpen(!isOpen)}
        >
          <span className="hr-pb-chevron" aria-hidden="true" />
          <span className="hr-pb-toggle-label">{toggleLabel}</span>
          {collapsedTotal ? <span className="hr-pb-toggle-total">{collapsedTotal}</span> : null}
        </button>
      ) : (
        // Heading setting is IGNORED in accordion style (WP parity — the
        // toggle is the heading there).
        config.heading ? <div className="hr-pb-heading">{config.heading}</div> : null
      )}
      <div className="hr-pb-clip" id={accordion ? panelId : undefined}>
        <PriceBreakdownRows {...rowsProps} />
      </div>
    </div>
  )
}
